import { useEffect } from 'react';
import { create } from 'zustand';

// Answers Explore is still writing, and one that finished while the reader
// was somewhere else. Small and here from the start, because the notice that
// an answer is ready shows on any screen; Explore's own code loads with its
// screens.

interface Answers {
  /** Conversations with an answer on its way that the reader asked for here. */
  coming: Record<string, true>;
  /** How many views of each conversation are on screen now. */
  watched: Record<string, number>;
  /** The conversation whose answer finished out of sight, until the reader opens it or the notice goes. */
  ready: { id: string; failed: boolean } | null;
}

export const useAnswers = create<Answers>(() => ({ coming: {}, watched: {}, ready: null }));

/** The reader asked here: when the answer ends out of their sight, they are told. */
export function expectAnswer(id: string): void {
  useAnswers.setState((s) => ({ coming: { ...s.coming, [id]: true } }));
}

/**
 * An answer ended: complete, stopped or failed (or its conversation was
 * removed). Heard from its own stream and from the events stream, whichever
 * comes first; the second finds nothing left to do. The reader is told only
 * of one they asked for here and aren't looking at, and not of one they
 * stopped themselves.
 */
export function answerEnded(id: string, status: string): void {
  const s = useAnswers.getState();
  if (!s.coming[id]) return;
  const { [id]: _gone, ...coming } = s.coming;
  const tell = !s.watched[id] && (status === 'complete' || status === 'failed');
  useAnswers.setState({ coming, ready: tell ? { id, failed: status === 'failed' } : s.ready });
}

export const dismissReady = (): void => useAnswers.setState({ ready: null });

/** How long the notice that an answer is ready stays, unless it is opened or put away. */
const READY_MS = 10_000;

/**
 * The answer that finished out of sight, for the one place that says so on
 * this screen: the app's toast, or the reader's corner. It is put away after
 * a moment of being shown. shown: false where nothing is said (a review
 * session), and the notice waits for the next screen.
 */
export function useReadyAnswer(shown = true): Answers['ready'] {
  const ready = useAnswers((s) => s.ready);
  useEffect(() => {
    if (!ready || !shown) return;
    const t = window.setTimeout(dismissReady, READY_MS);
    return () => window.clearTimeout(t);
  }, [ready, shown]);
  return shown ? ready : null;
}

/** While a view of the conversation is on screen, its answer finishing needs no notice. */
export function useWatchAnswer(id: string): void {
  useEffect(() => {
    useAnswers.setState((s) => ({
      watched: { ...s.watched, [id]: (s.watched[id] ?? 0) + 1 },
      ready: s.ready?.id === id ? null : s.ready,
    }));
    return () =>
      useAnswers.setState((s) => ({
        watched: { ...s.watched, [id]: Math.max(0, (s.watched[id] ?? 1) - 1) },
      }));
  }, [id]);
}
